import { Text } from '@/components/ui/text';
import { getEventTagLabel } from '@/features/events/eventTags';
import { formatWebFeedDistanceLabel } from '@/features/events/feed/feedFormatters';
import { getAbsoluteWebFeedHref } from '@/features/events/feed/webFeedLinks';
import { isWebFeedEventHappeningNow } from '@/features/events/feed/webFeedTiming';
import { getEventRSVPButtonLabel } from '@/features/events/eventMobileHelpers';
import type { FeedEvent } from '@/lib/api/apiTypes';
import { openDirections } from '@/lib/location/directions';
import { getEventHref } from '@/lib/navigation/routes';
import { shareLink } from '@/lib/platform/share';
import { showToast } from '@/lib/useToastStore';
import { router } from 'expo-router';
import * as React from 'react';
import { Pressable, View } from 'react-native';

export function WebDealStrip({
  compact,
  event,
  onRsvp,
}: {
  compact: boolean;
  event: FeedEvent;
  onRsvp: (event: FeedEvent) => void;
}) {
  const dealLabel = getWebDealEventLabel(event);
  const dealLabelColor = getWebDealEventLabelColor(dealLabel, event);
  const countdownLabel = getWebDealEventCountdown(event);
  const distanceLabel = formatWebFeedDistanceLabel(event.distance_meters);
  const eventHref = getEventHref(event.id, event.title, { source: 'web_feed' });
  const ctaLabel = getEventRSVPButtonLabel(event);

  const handleDirections = React.useCallback((pressEvent: any) => {
    pressEvent?.stopPropagation?.();
    if (typeof event.latitude !== 'number' || typeof event.longitude !== 'number') {
      showToast({ message: 'Directions are not available for this event yet.' });
      return;
    }
    openDirections(event.latitude, event.longitude).catch(() => undefined);
  }, [event.latitude, event.longitude]);

  const handleShare = React.useCallback(async (pressEvent: any) => {
    pressEvent?.stopPropagation?.();
    await shareLink({
      message: `${event.title} at ${event.venue_name} on Radious`,
      title: event.title,
      url: getAbsoluteWebFeedHref(eventHref),
    });
  }, [event.title, event.venue_name, eventHref]);

  return (
    <Pressable
      className="active:opacity-95"
      onPress={() => router.push(eventHref as any)}
      style={
        {
          backgroundColor: '#111',
          borderColor: 'rgba(255,255,255,0.10)',
          borderRadius: 12,
          borderWidth: 1,
          display: 'flex',
          flexDirection: compact ? 'column' : 'row',
          gap: compact ? 12 : 20,
          gridColumn: '1 / -1',
          justifyContent: compact ? 'flex-start' : 'space-between',
          alignItems: compact ? 'stretch' : 'center',
          marginVertical: 4,
          paddingHorizontal: compact ? 16 : 20,
          paddingVertical: 14,
          width: '100%',
        } as any
      }>
      <View className="min-w-0" style={{ flexBasis: compact ? undefined : 250, flexShrink: 1 } as any}>
        <Text
          className="text-[10px] font-medium uppercase tracking-[0.08em]"
          numberOfLines={1}
          style={{ color: dealLabelColor }}>
          {dealLabel}
        </Text>
        <Text className="mt-1 text-[15px] font-medium leading-[19px] text-white" numberOfLines={1}>
          {event.title}
        </Text>
      </View>

      <View className="min-w-0 flex-1">
        <View className="flex-row flex-wrap items-center gap-1.5">
          <Text
            className="min-w-0 shrink text-[12px] font-medium"
            numberOfLines={1}
            style={{ color: 'rgba(255,255,255,0.35)' }}>
            {event.venue_name}
          </Text>
          {distanceLabel ? <View className="size-1 rounded-full bg-white/20" /> : null}
          {distanceLabel ? (
            <Text className="text-[12px] font-medium" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {distanceLabel}
            </Text>
          ) : null}
          {countdownLabel ? <View className="size-1 rounded-full bg-white/20" /> : null}
          {countdownLabel ? (
            <Text className="text-[12px] font-medium" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {countdownLabel}
            </Text>
          ) : null}
        </View>
      </View>

      <View
        className="flex-row items-center gap-2"
        style={{ flexShrink: 0, flexWrap: compact ? 'wrap' : 'nowrap' }}>
        <Pressable
          onPress={(pressEvent) => {
            pressEvent?.stopPropagation?.();
            onRsvp(event);
          }}
          style={{
            backgroundColor: '#D85A30',
            borderRadius: 99,
            paddingHorizontal: 16,
            paddingVertical: 7,
          }}>
          <Text className="text-[12px] font-medium text-white">{ctaLabel}</Text>
        </Pressable>

        <Pressable
          onPress={handleDirections}
          style={{
            backgroundColor: 'rgba(255,255,255,0.08)',
            borderRadius: 99,
            paddingHorizontal: 16,
            paddingVertical: 7,
          }}>
          <Text className="text-[12px] font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Directions
          </Text>
        </Pressable>
        <Pressable
          onPress={handleShare}
          style={{
            backgroundColor: 'rgba(255,255,255,0.08)',
            borderRadius: 99,
            paddingHorizontal: 16,
            paddingVertical: 7,
          }}>
          <Text className="text-[12px] font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Share
          </Text>
        </Pressable>
      </View>
    </Pressable>
  );
}

function getEventDealTypeTag(event: FeedEvent) {
  return event.tags.find((tag) => {
    const normalized = tag.trim().toLowerCase();
    return (
      normalized.includes('deal') ||
      normalized.includes('special') ||
      normalized === 'happy_hour' ||
      normalized === 'happy-hour' ||
      normalized === 'open_bar' ||
      normalized === 'open-bar' ||
      normalized === 'free_food' ||
      normalized === 'free-food' ||
      normalized === 'cocktails' ||
      normalized === 'craft_beer' ||
      normalized === 'craft-beer' ||
      normalized === 'wine_tasting' ||
      normalized === 'wine-tasting'
    );
  });
}

function getWebDealEventLabel(event: FeedEvent) {
  return (
    event.promotion_label?.trim() ||
    event.editorial_label?.trim() ||
    (getEventDealTypeTag(event) ? getEventTagLabel(getEventDealTypeTag(event)!) : 'Deal')
  );
}

function getWebDealEventLabelColor(label: string, event: FeedEvent) {
  const upperLabel = label.toUpperCase();
  if (isWebFeedEventHappeningNow(event) || upperLabel.includes('LIVE')) return '#D85A30';
  if (upperLabel.includes('HAPPY HOUR')) return '#97C459';
  if (upperLabel.includes('DRINK')) return '#FAC775';
  return '#FAC775';
}

function getWebDealEventCountdown(event: FeedEvent) {
  if (isWebFeedEventHappeningNow(event)) return 'Live now';

  const startsAt = new Date(event.starts_at).getTime();
  const diffMs = startsAt - Date.now();
  if (!Number.isFinite(startsAt) || diffMs <= 0) return null;

  const hours = Math.ceil(diffMs / (60 * 60 * 1000));
  if (hours < 24) return `Next in ${hours}h`;

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `Next in ${days}d${remainingHours > 0 ? ` ${remainingHours}h` : ''}`;
}

